import _ from 'lodash';
import { jsonSchemaKeywords } from '@scale/shared/config/utils';

export function schemaPathToPretty(schemaPath: string) {
  return schemaPath
    .split('.')
    .filter(str => str !== 'properties' && str !== 'items')
    .map(word => {
      // Had to add the match SBQ case because it was breaking in disableSBQatSpecificReviewLevels
      // disableSBQatSpecificReviewLevels was being prettified to Disable SB Qat Specific ReviewLevels
      if (word.match(/SBQ[a-z]/g)) {
        word = word.replace(/(?<=SBQ)[a-z]/g, letter => letter.toUpperCase());
      }
      return _.startCase(word);
    })
    .join(' > ');
}

/**
 * For arrays, converts "items" in path to the corresponding index selected in the UI
 * Then it returns the corresponding config value for that path
 * e.g. If the user has the first item of graderConfigs.default (0th index) selected:
 * graderConfigs.default.items.whitelisted.items.cents --> graderConfigs.default[0].whitelisted[1].cents
 *
 * @param valuePath - Dot separated path
 * @param configs - Full set of current config values
 * @param indexMapping - Mapping of currently selected array indices in the UI
 * @return - Config value at the indexed path
 */
export function getValue(
  valuePath: string,
  configs: Record<string, any> | undefined,
  indexMapping: Partial<Record<string, number>>,
) {
  const segments = valuePath.split('.');
  let path = segments[0];
  let object: any = configs?.[path];
  for (let i = 1; i < segments.length; i++) {
    const segment = segments[i];
    if (segment === 'items' || segment === 'anyOf' || segment === 'oneOf') {
      const index = indexMapping[path] || 0;
      object = object?.[index!];
    } else if (segment === 'then' || segment === 'else') {
      // TODO: 'fix this logic'
      const index = indexMapping.then;
      object = object?.[index!];
    } else {
      object = object?.[segment];
    }
    path += '.' + segment;
  }
  return object;
}

/**
 * For arrays, converts "items" in path to the corresponding index selected in the UI
 * e.g. If the user has the first item of graderConfigs.default (0th index) selected:
 * graderConfigs.default.items.whitelisted.items.cents --> graderConfigs.default[0].whitelisted[1].cents
 *
 * @param valuePath - Dot separated path
 * @param indexMapping - Mapping of currently selected array indices in the UI
 * @return - Dot separated path
 */
export function getValuePath(valuePath: string, indexMapping: Partial<Record<string, number>>) {
  const segments = valuePath.split('.');
  let path = segments[0];
  let indexedPath = segments[0];
  for (let i = 1; i < segments.length; i++) {
    const segment = segments[i];
    if (segment === 'items') {
      const index = indexMapping[path] || 0;
      indexedPath += '.' + index;
    } else {
      indexedPath += '.' + segment;
    }
    path += '.' + segment;
  }
  return indexedPath;
}

/**
 * Convert from a JSON Schema definition path to a JSON Schema value object path
 *
 * @param schemaPath
 */
export function schemaPathToValuePath(schemaPath: string) {
  return schemaPath
    .split('.')
    .filter(segment => !jsonSchemaKeywords.includes(segment))
    .join('.');
}

/**
 * Convert from a JSON Schema value object path to a JSON Schema definition path
 *
 * @param pathDot
 */
export function valuePathToSchemaPath(pathDot: string) {
  return valuePathListToSchemaPath(splitDotSeparatedPath(pathDot));
}

/**
 * Splits a dot separated path into an array of segments
 * graderConfigs.default.items.whitelisted.items.cents --> ['graderConfigs', 'default', 'items', 'whitelisted', 'items', 'cents']
 *
 * @param pathDot - Dot separated path
 */
export function splitDotSeparatedPath(pathDot: string) {
  return pathDot.split('.');
}

/**
 * Converts a dot separated path into one that works with JSON Schema where children are stored in a properties field
 * graderConfigs.default.items.whitelisted.items.cents --> graderConfigs.properties.default.items.whitelisted.items.cents
 *
 * @param pathSegments - Path as an array (['graderConfigs', 'default', 'items', 'whitelisted', 'items', 'cents'])
 */
export function valuePathListToSchemaPath(pathSegments: string[]) {
  const joinedWithProperties = 'properties.' + pathSegments.join('.properties.');
  return joinedWithProperties.replace(/properties.items/g, 'items');
}

export function joinPath(a: string | number, b: string | number) {
  if (!a) return '' + b;
  if (!b) return '' + a;
  return a + '.' + b;
}
